import { Grade, ObjectiveGradingScale, request, waitAndGetRequestResponse } from 'react-common'
import { ActivityPart } from '@/api/models/activityParts'
import { Objective } from '@/api/models/objective'
import { ObjectiveRecord } from '@/api/models/objectiveRecord'
import { Activity, ActivityState, PresentationMode } from './activity'
import { ActivityRecord } from './activityRecord'

// @todo minor tech debt this file like a god object, it should be splitted as a personal responsibilities

export enum ObjectiveWorkflowState {
  inProgress = 1,
  submitted = 2,
  inGrading = 3,
  graded = 4,
  gradingApproved = 5,
  finished = 6,
}

export type ActivityWorkflowId = string
export type ActivityWorkflowAggregateId = string
export type ActivityWorkflow = {
  objectiveWorkflowId: ActivityWorkflowId
  activityPartIds: string[]
}

export type ActivityWorkflowUpdate = {
  activityWorkflowIds: ActivityWorkflowId[]
  progress: number
}

export type ActivityWorkflowFinish = {
  objectiveWorkflowId: ActivityWorkflowId
  activityWorkflowIds: string[]
}

export type ObjectiveWorkflowId = string

export type ActivityPartId = string
export type ActivityPathId = string

// ASSUMED ActivityPartId/ActivityPartId/ActivityPartId/ActivityPartId.....
export type ActivityPartAxis = `${ActivityPartId}/${ActivityPartId}`

export enum GradeRevealPolicy {
  None = 0,
  Never = 1,
  AfterFinish = 2,
  AfterDate = 3,
}

export type ObjectiveWorkflow = {
  id: ObjectiveWorkflowId
  state: ObjectiveWorkflowState
  objectiveRecordId?: string
  progress: number
  started: string
  submitted: string
  finished: string | null
  protectResults: boolean
}

export type ObjectiveAccess = {
  id: string
  objectiveId: string
  user: string
  tenantId: string
  dueDate: string
  availabilityDate: string
  availabilityEndDate?: string
  retake: boolean
  isMandatory: boolean
  created: string
  modified: string
  gradeRevealPolicy: GradeRevealPolicy
  gradeRevealDate: string
  termId?: string
  groupId?: string
  accessNewContentAfterCompletion: boolean
}

export type ObjectiveWorkflowAggregateId = string

export type ObjectiveWorkflowAggregate = {
  id: ObjectiveWorkflowAggregateId
  objective: Objective
  userId: string
  lastObjectiveRecord: ObjectiveRecord | null
  lastObjectiveWorkflow: ObjectiveWorkflow | null
  dueDate: string | null
  availabilityDate: string | null
  availabilityEndDate: string | null
  retake: boolean
  hasAccess: boolean
  objectiveAccess: ObjectiveAccess
  isSelfEnrolled: boolean
  hasWorkflow: boolean
  isArchived: boolean
  objectiveGradingScale: ObjectiveGradingScale
}

export type ObjectiveWorkflowAggregateQueryModel = {
  data: ObjectiveWorkflowAggregate[]
  totalCount: number
  groupCount: number
  summary: any
}

export type ObjectiveAccessesQueryModel = {
  data: LearnerObjectiveAccessCompactModel[]
  totalCount: number
  groupCount: number
  summary: any
}

export enum ReviewPolicy {
  Never = 1,
  AfterSubmission = 2,
  AfterRevealingResults = 3,
}

export enum ResultsDisclosurePolicy {
  Never = 1,
  AfterFinish = 2,
  AfterDate = 3,
  AfterScore = 4,
}

export enum ActivityWorkflowState {
  inProgress = 1,
  submitted = 2,
  inGrading = 3,
  graded = 4,
  gradingApproved = 5,
  finished = 6,
}
export type LastActivityWorkflowId = ActivityWorkflowId
export type LastActivityWorkflow = {
  id: LastActivityWorkflowId
  userId: string
  state: ActivityWorkflowState
  started: string
  finished: string
  submitted: string
  progress: number
  score: number
  lastProctoringSessionId: string
  groupActivityWorkflowId: string
}

export type ActivityWorkflowAggregate = {
  id: string
  userId: string
  activity: Activity
  lastActivityWorkflow: LastActivityWorkflow | null
  lastActivityWorkflowId: string | null
  lastActivityRecord: ActivityRecord
  children: ActivityPart<Activity>[]
  activityRecordsCount: number
  bestActivityRecord?: ActivityRecord
  bestActivityRecordScore?: number
  activityConstraint: ActivityConstraintReducedModel
}

export type GroupActivityWorkflowDiscussion = {
  id: string
  channelId: string
}

export type GroupActivityWorkflow = {
  id: string
  activity: Activity
  group: { id: string; name: string; channelId: string }
  state: ActivityWorkflowState
  created: string
  started: string
  submitted: string | null
  graded: string | null
  gradeApproved: string | null
  finished: string | null
  grade: string | null
  score: number | null
  progress: number | null
  groupActivityRecordId: string | null
  discussion: GroupActivityWorkflowDiscussion
}

export type ActivityWithAggregate = Activity & {
  activityWorkflowAggregate: ActivityWorkflowAggregate | null
  children: ActivityPart<ActivityWithAggregate>[]
  activityPart: ActivityPart
  available: boolean
}

// @todo use it from react-common
export type ActivityConstraintReducedModel = {
  startFrom?: string | null
  startUntil?: string | null
  due?: string | null
  gracePeriodUntil?: string | null
  accessibleUntil?: string | null
  revealResultsFrom?: string | null
  collaborative: boolean
  resultsDisclosureAbove?: number | null
  resultsDisclosureFrom?: string | null
  resultsDisclosurePolicy?: ResultsDisclosurePolicy | null
  reviewPolicy?: ReviewPolicy | null
}

export type LearnerActivityCompactModel = {
  id: string
  state: ActivityState
  childrenCount: number
  presentationMode: PresentationMode
}

export type LearnerObjectiveCompactModel = {
  id: string
  activity: LearnerActivityCompactModel
  code: string
  name: string
  description: string
  imageUrl: string | null
  showcases: {
    id: string
    name: string
  }[]
  gradingScaleId?: string
  announcementChannelId?: string
}
export type GradingScale = {
  id: string
  name: string
  description: string
  maxScore: number
  grades: Grade[]
  passingGradeId: string
}

export type LearnerObjectiveWorkflowAggregateModel = {
  id: string
  isSelfEnrolled: boolean
  lastObjectiveRecord: ObjectiveRecord | null
  lastObjectiveWorkflow: ObjectiveWorkflow
  // this objectiveGradingScale doesn't come from the BE, we fetch all grading scales and find the one that matches the objective
  objectiveGradingScale?: ObjectiveGradingScale | null
}

export type LearnerObjectiveAccessCompactModel = {
  id: string
  availabilityDate: string | null
  availabilityEndDate: string | null
  dueDate: string | null
  gradeRevealDate: string | null
  gradeRevealPolicy: GradeRevealPolicy
  isMandatory: boolean
  issueCertificate: boolean
  accessNewContentAfterCompletion: boolean
  retake: boolean
  showInCatalog: boolean
  termId: string | null
  created: string
  modified: string
  objective: LearnerObjectiveCompactModel
  objectiveWorkflowAggregate: LearnerObjectiveWorkflowAggregateModel
  userId: string
  objectiveId: string
}

export type LearnerObjectiveAccessObjectiveWorkflow = Pick<
  ObjectiveWorkflowAggregate,
  'id' | 'isSelfEnrolled' | 'lastObjectiveRecord' | 'lastObjectiveWorkflow'
> & { lastObjectiveWorkflow: (ObjectiveWorkflow & { userId: string | null }) | null }

export type LearnerObjectiveAccessFullModel = {
  id: string
  availabilityDate: string | null
  availabilityEndDate: string | null
  dueDate: string | null
  gradeRevealDate: string | null
  gradeRevealPolicy: GradeRevealPolicy
  isMandatory: boolean
  issueCertificate: boolean
  accessNewContentAfterCompletion: boolean
  retake: boolean
  showInCatalog: boolean
  termId: string | null
  created: string
  modified: string
  objective: Objective & { objectiveGradingScale: ObjectiveGradingScale | null }
  objectiveWorkflowAggregate: LearnerObjectiveAccessObjectiveWorkflow | null
  userId: string
  objectiveId: string
}

export class AggregateApi {
  private static readonly api = request('objective-workflow-aggregates/:id/:resource?')
  private static readonly objectiveWorkflowStarts = request('start-objective-workflows')
  private static readonly objectiveWorkflowSubmits = request('submit-objective-workflows')
  private static readonly activityWorkflowStarts = request('start-activity-workflows')
  private static readonly activityWorkflowFinish = request('finish-activity-workflows')
  private static readonly activityWorkflows = request('users/me/objective-workflows/:ow_id/activity-workflows/:aw_id')
  private static readonly byObjective = request('objectives/:id/objective-workflow-aggregate')
  private static readonly objectiveWorkflowAggregatesQuery = request('objective-workflow-aggregates/query')
  private static readonly workflow = request('objective-workflows/:id')
  private static readonly myAggregates = request('users/me/objective-workflows/:id/activity-workflow-aggregates/query')
  private static readonly groupActivityWorkflows = request(
    'users/me/objective-workflow-aggregates/:owa_id/group-activity-workflows/:gaw_id',
  )

  static startObjectiveWorkflow(objectiveWorkflowAggregateId: ObjectiveWorkflowAggregateId, id: string) {
    return this.objectiveWorkflowStarts
      .post({
        body: {
          objectiveWorkflowAggregateId,
          id,
        },
        headers: {
          prefer: 'respond-sync',
        },
      })
      .then((result) => result.body as ObjectiveWorkflowAggregate)
  }

  static submitObjectiveWorkflow(id: string) {
    return this.objectiveWorkflowSubmits.post({ body: { id } }).then((result) => waitAndGetRequestResponse(result.body))
  }

  static startActivityWorkflow(data: ActivityWorkflow) {
    return this.activityWorkflowStarts
      .post({
        body: {
          objectiveWorkflowId: data.objectiveWorkflowId,
          activityPartIds: data.activityPartIds,
        },
        headers: {
          prefer: 'respond-sync',
        },
      })
      .then((result) => result.body as ActivityWorkflowAggregate)
  }

  static finishActivityWorkflow(data: ActivityWorkflowFinish) {
    return this.activityWorkflowFinish
      .post({
        body: {
          objectiveWorkflowId: data.objectiveWorkflowId,
          activityWorkflowIds: data.activityWorkflowIds,
        },
        headers: {
          prefer: 'respond-sync',
        },
      })
      .then((result) => result.body as ActivityWorkflowAggregate)
  }

  static getRecords(id: string) {
    return this.api.get({ url: { id, resource: 'objective-records' } }).then((res) => res.body as ObjectiveRecord[])
  }

  static getByObjective(id: string) {
    return this.byObjective.get({ url: { id } }).then((res) => res.body as ObjectiveWorkflowAggregate)
  }

  static getObjectiveWorkflowAggregatesWithQuery(query: Record<string, any>, shouldSort: boolean = true) {
    const sort = `[{ selector: 'objective.name', desc: false }]`

    return this.objectiveWorkflowAggregatesQuery
      .get({
        query: {
          ...query,
          ...(shouldSort ? { sort } : {}),
        },
      })
      .then((res) => res.body?.data as ObjectiveWorkflowAggregate[])
  }

  static getWorkflow(id: string) {
    return this.workflow.get({ url: { id } }).then((res) => res.body as ObjectiveWorkflow)
  }

  static myAggregatesByQuery({
    queries,
    objectiveWorkflowId,
  }: {
    queries: Record<string, any>
    objectiveWorkflowId: ObjectiveWorkflowId
  }) {
    return this.myAggregates
      .get({ url: { id: objectiveWorkflowId }, query: queries })
      .then((res) => res.body.data as ActivityWorkflowAggregate[])
  }

  static updateActivityWorkflow(objectiveWorkflowId: ObjectiveWorkflowId, payload: ActivityWorkflowUpdate) {
    return this.activityWorkflows
      .put({
        url: {
          ow_id: objectiveWorkflowId,
          aw_id: payload.activityWorkflowIds.at(-1),
        },
        body: payload,
      })
      .then((res) => res.body as ActivityWorkflowAggregate)
  }

  static getGroupActivityWorkflows({
    objectiveWorkflowAggregateId,
    groupActivityWorkflowId,
  }: {
    objectiveWorkflowAggregateId: ObjectiveWorkflowAggregateId
    groupActivityWorkflowId: string
  }) {
    return this.groupActivityWorkflows
      .get({
        url: {
          owa_id: objectiveWorkflowAggregateId,
          gaw_id: groupActivityWorkflowId,
        },
      })
      .then((res) => res.body as GroupActivityWorkflow)
  }
}
